import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Radio } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import {
  DragReorderRow,
  isTrivialDragResult,
  ReorderPopover,
  toggleReorderPopoverAnchor,
} from 'components/common/utils';
import { CustomTabPanel, getTablePreferences, setTablePreferences } from 'components/utils';
import {
  applySavedColumnsState,
  DataGridColumn,
  getManageableColumnsDisplayOrder,
  reorderManageableColumnsByDisplayIndex,
  SavedColumn,
  toSavedVisibleColumns,
} from './utils';
import { OpenInPreference } from './RecordOpenCell';

const COLUMNS_TAB_VALUE = 0;
const OPEN_IN_TAB_VALUE = 100;

const OPEN_IN_OPTIONS: { value: OpenInPreference; label: string }[] = [
  { value: 'sidePanel', label: 'Side panel' },
  { value: 'fullPage', label: 'Full page' },
];

export type ColumnsManagerTab = {
  label: string;
  value: number;
  content: React.ReactNode;
};

const columnsManagerTabPanelSx = { marginTop: '10px', padding: 0 };

const ColumnsManager = ({
  name,
  initialColumns,
  fetchList,
  reloadKey,
  showAsTabs = false,
  tabs = [],
  showOpenIn = false,
  onOpenInChange,
}: {
  name: string;
  initialColumns: DataGridColumn[];
  fetchList: () => void;
  reloadKey?: number;
  showAsTabs?: boolean;
  tabs?: ColumnsManagerTab[];
  showOpenIn?: boolean;
  onOpenInChange?: (openIn: OpenInPreference) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tabValue, setTabValue] = useState(COLUMNS_TAB_VALUE);
  const [openIn, setOpenIn] = useState<OpenInPreference>('sidePanel');

  useEffect(() => {
    const saved = getTablePreferences(name).openIn;
    setOpenIn(saved === 'fullPage' ? 'fullPage' : 'sidePanel');
  }, [name]);

  const savedColumns = (getTablePreferences(name).columns || []) as SavedColumn[];
  const initialColumnsStateValue = applySavedColumnsState({ columns: initialColumns, savedColumns });

  const [columns, setColumns] = useState<DataGridColumn[]>(initialColumnsStateValue);

  useEffect(() => {
    const latestSavedColumns = (getTablePreferences(name).columns || []) as SavedColumn[];

    const updatedColumns = applySavedColumnsState({ columns: initialColumns, savedColumns: latestSavedColumns });

    setColumns(updatedColumns);
  }, [name, initialColumns, reloadKey]);

  const saveChanges = (updatedColumns: SavedColumn[]) => {
    setTablePreferences({
      tableName: name,
      values: [
        {
          key: 'columns',
          value: updatedColumns,
        },
      ],
    });

    fetchList();
  };

  const persistVisibleColumns = (updatedColumns: DataGridColumn[]) => {
    saveChanges(toSavedVisibleColumns(updatedColumns));
  };

  const onChange = (column: DataGridColumn) => {
    const savedColumns = (getTablePreferences(name).columns || []) as SavedColumn[];

    const updatedColumns: DataGridColumn[] = [];

    for (const col of columns) {
      const savedColumn = savedColumns.find(cl => cl.name === col.name);
      col.width = savedColumn?.width || col.width || undefined;

      if (col.name === column.name) {
        updatedColumns.push({ ...col, isVisible: !column.isVisible });
      } else {
        updatedColumns.push(col);
      }
    }

    setColumns(updatedColumns);
    persistVisibleColumns(updatedColumns);
  };

  const onDragEnd = (result: DropResult) => {
    if (isTrivialDragResult(result)) {
      return;
    }

    const updatedColumns = reorderManageableColumnsByDisplayIndex({
      columns,
      sourceIndex: result.source.index,
      targetIndex: result.destination!.index,
    });

    setColumns(updatedColumns);
    persistVisibleColumns(updatedColumns);
  };

  const renderLabel = (column: DataGridColumn) => {
    if (column.title) {
      return column.title;
    }
  };

  const closePopover = () => {
    setAnchorEl(null);

    if (showAsTabs || showOpenIn) {
      setTabValue(COLUMNS_TAB_VALUE);
    }
  };

  const renderColumnsList = () => (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="columns-manager-list">
        {provided => (
          <Box ref={provided.innerRef} {...provided.droppableProps}>
            {getManageableColumnsDisplayOrder(columns).map((column, index) => {
              const columnId = column.name || `column-${index}`;

              return (
                <Draggable key={columnId} draggableId={columnId} index={index}>
                  {(draggableProvided, snapshot) => (
                    <DragReorderRow
                      draggableProvided={draggableProvided}
                      snapshot={snapshot}
                      label={renderLabel(column)}
                      trailing={
                        <Checkbox
                          checked={Boolean(column.isVisible)}
                          onChange={() => {
                            onChange(column);
                          }}
                          sx={{ flexShrink: 0, p: 0.5 }}
                        />
                      }
                    />
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );

  const renderTabs = () => {
    const tabItems = [
      { label: 'Columns', value: COLUMNS_TAB_VALUE },
      ...(showOpenIn ? [{ label: 'Open in', value: OPEN_IN_TAB_VALUE }] : []),
      ...(showAsTabs ? tabs.map(tab => ({ label: tab.label, value: tab.value })) : []),
    ];

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        {tabItems.map(tab => {
          const active = tabValue === tab.value;

          return (
            <Box
              key={tab.value}
              onClick={() => {
                setTabValue(tab.value);
              }}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                p: '5px 10px',
                borderBottom: theme => `2px solid ${active ? theme.palette.text.primary : 'transparent'}`,
                color: active ? 'text.primary' : 'text.secondary',
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>
    );
  };

  const chooseOpenIn = (next: OpenInPreference) => {
    if (next === openIn) {
      return;
    }

    setOpenIn(next);
    setTablePreferences({
      tableName: name,
      values: [{ key: 'openIn', value: next }],
    });
    onOpenInChange?.(next);
  };

  const renderOpenIn = () => {
    return (
      <Box sx={{ px: 0.5, py: 0.5 }}>
        {OPEN_IN_OPTIONS.map(option => (
          <Box
            key={option.value}
            onClick={() => chooseOpenIn(option.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 28,
              px: 0.5,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 13,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Radio size="small" checked={openIn === option.value} tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
            {option.label}
          </Box>
        ))}
      </Box>
    );
  };

  const renderPopoverContent = () => {
    if (!showAsTabs && !showOpenIn) {
      return renderColumnsList();
    }

    return (
      <>
        {renderTabs()}

        <CustomTabPanel value={tabValue} index={COLUMNS_TAB_VALUE} style={columnsManagerTabPanelSx} keepMounted>
          {renderColumnsList()}
        </CustomTabPanel>

        {showOpenIn ? (
          <CustomTabPanel value={tabValue} index={OPEN_IN_TAB_VALUE} style={columnsManagerTabPanelSx} keepMounted>
            {renderOpenIn()}
          </CustomTabPanel>
        ) : null}

        {showAsTabs
          ? tabs.map(tab => (
              <CustomTabPanel
                key={tab.value}
                value={tabValue}
                index={tab.value}
                style={columnsManagerTabPanelSx}
                keepMounted
              >
                {tab.content}
              </CustomTabPanel>
            ))
          : null}
      </>
    );
  };

  const renderColumns = () => {
    return (
      <ReorderPopover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closePopover}>
        {renderPopoverContent()}
      </ReorderPopover>
    );
  };

  return (
    <Box>
      <Button
        onClick={event => {
          toggleReorderPopoverAnchor(setAnchorEl, event);
        }}
        variant="contained"
        color="inherit"
      >
        Options
      </Button>

      {renderColumns()}
    </Box>
  );
};

export default ColumnsManager;
